import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
  ConfigApi,
} from '@backstage/core-plugin-api';

export interface JiraIssue {
  id: string;
  key: string;
  _jiraInstance?: {
    name: string;
    baseUrl: string;
  };
  fields: {
    summary: string;
    status: {
      name: string;
      statusCategory: {
        key: string;
        colorName: string;
      };
    };
    priority?: {
      name: string;
      iconUrl: string;
    };
    assignee?: {
      displayName: string;
      avatarUrls: {
        '24x24'?: string;
        '48x48': string;
      };
    };
    created: string;
    updated: string;
    issuetype: {
      name: string;
      iconUrl: string;
    };
  };
}

export interface JiraProject {
  id: string;
  key: string;
  name: string;
}

export const jiraApiRef = createApiRef<JiraApi>({
  id: 'plugin.jira.service',
});

export class JiraApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
    private readonly configApi: ConfigApi,
  ) {}

  async getProjectIssues(projectKey: string): Promise<JiraIssue[]> {
    return this.searchIssues(projectKey, '');
  }

  async searchIssues(
    projectKey: string = '*',
    textSearch: string = '',
    statusFilter: string = '',
    assigneeFilter: string = '',
    issueTypeFilter: string = '',
  ): Promise<JiraIssue[]> {
    const backendUrl = this.configApi.getString('backend.baseUrl');
    const params = new URLSearchParams();
    params.append('project', projectKey);
    if (textSearch) {
      params.append('text', textSearch);
    }
    if (statusFilter) {
      params.append('status', statusFilter);
    }
    if (assigneeFilter) {
      params.append('assignee', assigneeFilter);
    }
    if (issueTypeFilter) {
      params.append('issueType', issueTypeFilter);
    }
    params.append('maxResults', '50');

    const response = await this.fetchApi.fetch(
      `${backendUrl}/api/jira/search?${params.toString()}`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch Jira issues: ${response.statusText}`);
    }

    const data = await response.json();
    return data.issues || [];
  }

  async getMultipleProjectIssues(projectKeys: string[]): Promise<JiraIssue[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const jql = `project IN (${projectKeys.join(',')}) ORDER BY updated DESC`;
    const fields = 'summary,status,priority,assignee,created,updated';

    const response = await this.fetchApi.fetch(
      `${baseUrl}/jira/api/rest/api/3/search/jql?jql=${encodeURIComponent(
        jql,
      )}&maxResults=100&fields=${fields}`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch Jira issues: ${response.statusText}`);
    }

    const data = await response.json();
    return data.issues || [];
  }

  async getAllProjects(): Promise<any[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/jira/api/rest/api/3/project/search?maxResults=100`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch Jira projects: ${response.statusText}`);
    }

    const data = await response.json();
    return data.values || [];
  }

  async getProject(projectKey: string): Promise<JiraProject> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/jira/api/rest/api/3/project/${projectKey}`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch Jira project: ${response.statusText}`);
    }

    return await response.json();
  }
}
